import {
    AddResponse,
    UpdateResponse,
    UpdateRequest,
    ShoppingCart,
    ShoppingCartItem,
    ShoppingCartItems,
    ShoppingCarts,
    Status,
    AddToCartResponse,
    AddToPurchaseList,
} from './shopping-cart.model';
import { unlockForm } from '../form-fields/form-fields';
import { debounce } from '../../100-components/helper';
import { showToastAlert, AlertType, clearToastAlerts } from '../alerts/alerts';
import { updateCartWidget } from './shopping-cart-widget';
import { FormFieldsController } from '../form-fields/form-fields.stimulus';

const ATTRIBUTE_SHOPPING_CART = 'data-c-shopping-cart';
const ATTRIBUTE_SHOPPING_CART_UPDATE = 'data-c-shopping-cart-update';
const ATTRIBUTE_SHOPPING_CART_DELETE = 'data-c-shopping-cart-delete';
const ATTRIBUTE_SHOPPING_CART_ITEM = 'data-klt-c-shopping-cart-item';
const ATTRIBUTE_SHOPPING_CART_CANDIDATE = 'data-klt-c-shopping-cart-candidate';
const ATTRIBUTE_SHOPPING_CART_PRICE = 'data-klt-c-shopping-cart-price';
const ATTRIBUTE_SHOPPING_CART_COUPON = 'data-klt-c-shopping-cart-coupon';
const ATTRIBUTE_SHOPPING_CART_AMOUNT = 'data-klt-c-shopping-cart-amount';
const ATTRIBUTE_SHOPPING_CART_TOTAL = 'data-klt-c-shopping-cart-total';
const ATTRIBUTE_SHOPPING_CART_PRICE_SUBTOTAL = 'data-klt-c-shopping-cart-price-subtotal';
const ATTRIBUTE_SHOPPING_CART_ISBN = 'data-klt-c-shopping-cart-isbn';
const ATTRIBUTE_SHOPPING_CART_ACTION = 'data-c-shopping-cart-action';
const ATTRIBUTE_SHOPPING_CART_PRODUCT_QUANTITY_IDENTIFIER = 'data-klt-c-shopping-cart-product-quantity-identifier';
const ATTRIBUTE_SHOPPING_CART_PRODUCT_QUANTITY_SYNC = 'data-klt-c-shopping-cart-product-quantity-sync';
const CLASS_SHOPPING_CART_SUCCESS = 'klt-c-button--success';
const PRODUCT_META_HIDDEN_FIELD_NAME = 'productIsbns';

const enum ShoppingCartActionType {
    AddToCart = 'addToCart',
    AddToPurchaseList = 'addToPurchaseList',
}

interface ShoppingCartCandidateProduct {
    id: string;
    masterElement: HTMLInputElement;
}

let carts: ShoppingCarts = {};
let cartCandidateProducts: ShoppingCartCandidateProduct[] = [];

/**
 * In the purchase list we have a "Put all the products on the list into the cart"
 * functionality. As we can't nest forms in forms due to HTML limitatations, which means
 * we can't wrap a form around a list of product that already contain a form  with the quantity
 * per product, we have a second form with hidden fields with all product ISBNs and the quantity.
 * Now when someone changes the quantity on a product in the purchase list, we have to sync this change
 * back to the hidden fields. This allows us to bulk add products to the cart.
 *
 * Here you can get that hidden field by the ISBN number to make updates to the quantity
 */
const getProductMetaHiddenField = (isbn13: number) =>
    document.querySelectorAll(`${PRODUCT_META_HIDDEN_FIELD_NAME}[${isbn13}]`);

/**
 * Init all cart coupled client-site functionality
 */
export default function init() {
    try {
        initCart();
        initCartCandidates();
        initProductQuantitySync();
    } catch (e) {
        console.error(`Couldn't initalize shopping cart module.`, e);
    }
}

/**
 * Init the cart used on a single page to show the items in the cart
 */
function initCart() {
    const shoppingCarts = document.querySelectorAll(`[${ATTRIBUTE_SHOPPING_CART}]`);

    for (let i = 0; i < shoppingCarts.length; i++) {
        // We build a virtual collection of all cart components in the page, including all items.
        // This helps to update the DOM state by manipulating the virtual, cached information.
        const cart = shoppingCarts[i];
        const cartId = cart.getAttribute(`${ATTRIBUTE_SHOPPING_CART}`) || '0';
        const updateCallback = cart.getAttribute(`${ATTRIBUTE_SHOPPING_CART_UPDATE}`);
        const subtotalElement = cart.querySelector(`[${ATTRIBUTE_SHOPPING_CART_PRICE_SUBTOTAL}]`);
        if (updateCallback) {
            const rows = cart.querySelectorAll(`[${ATTRIBUTE_SHOPPING_CART_ITEM}]`);

            if (rows.length > 0) {
                const shoppingCartItems: ShoppingCartItem[] = Array.from(rows).map((row) => {
                    const id = row.getAttribute(`${ATTRIBUTE_SHOPPING_CART_ITEM}`);
                    const amountField = row.querySelector(`[${ATTRIBUTE_SHOPPING_CART_AMOUNT}]`);
                    const priceField = row.querySelector(`[${ATTRIBUTE_SHOPPING_CART_PRICE}]`);
                    const couponField = row.querySelector(`[${ATTRIBUTE_SHOPPING_CART_COUPON}]`);
                    const totalField = row.querySelectorAll(`[${ATTRIBUTE_SHOPPING_CART_TOTAL}]`);
                    const isbnField = row.querySelector(`[${ATTRIBUTE_SHOPPING_CART_ISBN}]`);
                    const isbn = isbnField.getAttribute(`${ATTRIBUTE_SHOPPING_CART_ISBN}`);

                    if (amountField) {
                        const changeDebounceFn = debounce(function () {
                            onCartItemChange(cartId, id);
                        }, 600);

                        amountField.addEventListener('change', changeDebounceFn);
                        couponField.addEventListener('change', changeDebounceFn);
                    }

                    return {
                        index: id,
                        element: row,
                        isbn,
                        amountFieldElement: amountField as HTMLInputElement,
                        priceFieldElement: priceField as HTMLInputElement,
                        couponFieldElement: couponField as HTMLInputElement,
                        totalFieldElement: totalField as NodeListOf<HTMLElement>,
                    };
                });

                // Converts array to a key/value object which allows us to get a specific cart item by id
                const items: ShoppingCartItems = {};
                shoppingCartItems.forEach((item) => {
                    items[item.index] = item;
                });

                carts[cartId] = carts[cartId] || {
                    id: cartId,
                    element: cart,
                    subtotalElement,
                    updateCallback,
                    items,
                };
            }
        }
    }
}

/**
 * Inits AJAX functionality for all elements in the DOM that are marked as forms that contribute to the shopping cart
 */
function initCartCandidates() {
    const shoppingCartCandidates = document.querySelectorAll(`[${ATTRIBUTE_SHOPPING_CART_CANDIDATE}]`);

    for (let i = 0; i < shoppingCartCandidates.length; i++) {
        const candidate = shoppingCartCandidates[i] as HTMLFormElement;
        const addToCartTrigger = candidate.querySelector(
            `[${ATTRIBUTE_SHOPPING_CART_ACTION}="${ShoppingCartActionType.AddToCart}"`
        ) as HTMLButtonElement;

        const addToPurchaseListTrigger = candidate.querySelector(
            `[${ATTRIBUTE_SHOPPING_CART_ACTION}="${ShoppingCartActionType.AddToPurchaseList}"`
        ) as HTMLButtonElement;

        // Save quantity and identifier, which currently is one of the ISBN number formats, in an array.
        // This is later used to sync quantity fields between each other e.g. for the bulk add functionality
        // in the purchase list, which is a separate formular with all products as hidden fields.
        const quantityField = candidate.querySelector(
            `input[${ATTRIBUTE_SHOPPING_CART_PRODUCT_QUANTITY_IDENTIFIER}]`
        ) as HTMLInputElement;
        if (quantityField) {
            const id = quantityField.getAttribute(ATTRIBUTE_SHOPPING_CART_PRODUCT_QUANTITY_IDENTIFIER);
            const masterElement = quantityField;
            cartCandidateProducts.push({
                id,
                masterElement,
            });
        }

        if (addToCartTrigger && !addToCartTrigger.disabled) {
            addToCartTrigger.addEventListener('click', (e) => onAddToCart(e, candidate, addToCartTrigger));
        }

        if (addToPurchaseListTrigger && !addToPurchaseListTrigger.disabled) {
            addToPurchaseListTrigger.addEventListener('click', (e) =>
                onAddToPurchaseList(e, candidate, addToPurchaseListTrigger)
            );
        }
    }

    return shoppingCartCandidates;
}

/**
 * Used in the purchase list to add all products on the purchase list to the cart.
 * This function initalizes the connection between the hidden fields of the bulk add
 * form and the actual quantity input fields of the product lists.
 */
function initProductQuantitySync() {
    const formsToSync = document.querySelectorAll(`[${ATTRIBUTE_SHOPPING_CART_PRODUCT_QUANTITY_SYNC}]`);

    if (cartCandidateProducts.length > 0 && formsToSync.length > 0) {
        for (let i = 0; i < formsToSync.length; i++) {
            const form = formsToSync[i] as HTMLFormElement;
            const quantityFields = form.querySelectorAll(`[${ATTRIBUTE_SHOPPING_CART_PRODUCT_QUANTITY_IDENTIFIER}]`);
            const fieldSync = {};

            for (let qi = 0; qi < quantityFields.length; qi++) {
                const quantityField = quantityFields[qi];
                fieldSync[quantityField.getAttribute(ATTRIBUTE_SHOPPING_CART_PRODUCT_QUANTITY_IDENTIFIER)] =
                    quantityField;
            }

            form.addEventListener('submit', (e) => {
                cartCandidateProducts.forEach((productQuantity) => {
                    const currentQuantity = productQuantity.masterElement.value;
                    fieldSync[productQuantity.id].setAttribute('value', currentQuantity);
                });

                return true;
            });
        }
    }
}

async function onAddToCart(e, candidate: HTMLFormElement, addToCartTrigger: HTMLButtonElement, action?: string) {
    clearToastAlerts();

    const url = action || candidate.action;
    const body = new FormData(candidate);
    const form = candidate;

    try {
        body.append(addToCartTrigger.getAttribute('name'), addToCartTrigger.getAttribute('value'));

        addTo<AddToCartResponse>(url, body, form)
            .then(({ data, form }) => applyState(form, addToCartTrigger, data))
            .then((data) => {
                if (data.basket && data.basket.cart) {
                    // Handle cart widget update
                    const cart = data.basket.cart.item_count;
                    updateCartWidget({ cart });
                }
            });

        e.preventDefault();
    } catch (e) {
        console.error(e);
    }
}

async function onAddToPurchaseList(
    e: Event,
    candidate: HTMLFormElement,
    addToPurchaseListTrigger: HTMLButtonElement,
    action?: string
) {
    clearToastAlerts();

    const url = addToPurchaseListTrigger.getAttribute('formaction') || candidate.action;
    const body = new FormData(candidate);
    const form = candidate;

    try {
        body.append(addToPurchaseListTrigger.getAttribute('name'), addToPurchaseListTrigger.getAttribute('value'));
        addTo<AddToPurchaseList>(url, body, form)
            .then(({ data, form }) => applyState(form, addToPurchaseListTrigger, data))
            .then((data) => {
                if (data.shopping_list && data.shopping_list.list && data.status === 'success') {
                    const purchaseList = data.shopping_list.list.item_count;
                    updateCartWidget({ purchaseList });
                }
            });

        e.preventDefault();
    } catch (e) {
        console.error(e);
        form.submit();
    }
}

/**
 * Handler for changes in the DOM of the cart
 * @param {string} cartId
 * @param {string} itemId
 */
function onCartItemChange(cartId: string, itemId: string) {
    const shopCart = carts[cartId];
    const shopCartItem = shopCart.items[itemId];
    if (shopCart && shopCart.updateCallback) {
        const updateCallback = shopCart.updateCallback;
        const isbn = shopCartItem.isbn;
        const quantity = parseInt(shopCartItem.amountFieldElement.value);
        const coupon = shopCartItem.couponFieldElement.value.trim();
        let action = 'changequantity';

        if (coupon) {
            action = 'changecoupon';
        }

        updateCart(updateCallback, {
            isbn,
            quantity,
            coupon,
            action,
        })
            .then((response) => {
                const data = response as UpdateResponse;
                shopCartItem.amountFieldElement.value = data.quantity.toString();

                // It's possible that there are multiple total fields and some of them aren't input fields, but HTML elements.
                // in this case we don't have the value property.
                Array.prototype.forEach.call(shopCartItem.totalFieldElement, (element) => {
                    if (element.value) {
                        element.value = data.subtotal;
                    } else {
                        element.innerHTML = data.subtotal;
                    }
                });

                shopCart.subtotalElement.innerHTML = data.total;
            })
            .catch((error) => console.log(error.message));
    }
}

/**
 * Add item to cart or purchase liston the back-end with the provided parameters
 */
async function addTo<T extends AddResponse>(
    url: string,
    body: FormData,
    form?: HTMLFormElement
): Promise<{ data: T; form: HTMLFormElement }> {
    const method = form?.method ? form.method || 'POST' : 'POST';
    body = method.toUpperCase() === 'GET' ? null : body;

    const init: RequestInit = {
        credentials: 'include',
        cache: 'no-cache',
        headers: {
            Accept: 'application/json',
        },
        method,
        body,
    };

    const response = await fetch(url, init);

    const data = await response.json();
    return { data, form };
}

function applyState<T extends AddResponse>(form: HTMLFormElement, submitButton: HTMLButtonElement, data: T) {
    if (data.status === 'success') {
        applySuccessState(form, submitButton, data);
    } else {
        applyErrorState(form, data);
    }

    return data;
}

function applySuccessState<T extends AddResponse>(form: HTMLFormElement, submitButton: HTMLButtonElement, data: T) {
    unlockForm(form);
    let alertType: AlertType = AlertType.Success;
    const basketButton = form.querySelector('button[type="submit"]') as HTMLButtonElement;

    if (data.status === Status.Error) {
        alertType = AlertType.Error;
    }

    if (submitButton) {
        submitButton.classList.add(CLASS_SHOPPING_CART_SUCCESS);
        submitButton.disabled = true;
        setTimeout(() => {
            submitButton.disabled = false;
            submitButton.classList.remove(CLASS_SHOPPING_CART_SUCCESS);
        }, 1000);
    }

    showToastAlert(
        {
            title: data.message,
        },
        alertType
    );

    // Caution: Flaky selector
    const quantityInputField = form.querySelector<HTMLInputElement>('input');

    if (quantityInputField) {
        FormFieldsController.markInputValid(quantityInputField);
    }

    return data;
}

function applyErrorState<T extends AddResponse>(form: HTMLFormElement, data: T) {
    const alertType = AlertType.Error;

    showToastAlert(
        {
            title: data.message,
        },
        alertType
    );

    // Caution: Flaky selector
    const quantityInputField = form.querySelector<HTMLInputElement>('input');

    if (quantityInputField) {
        FormFieldsController.markInputInvalid(quantityInputField, data.message);
    }

    return data;
}

/**
 * Call the API for updating the cart
 * @param url
 * @returns {Promise<any>}
 */
async function updateCart(url, body: UpdateRequest) {
    var formData = new FormData();
    formData.append('isbn', body.isbn);
    formData.append('action', body.action);

    if (body.quantity) {
        formData.append('quantity', body.quantity.toString());
    }

    if (body.coupon) {
        formData.append('coupon', body.coupon);
    }

    const response = await fetch(url, {
        cache: 'no-cache',
        method: 'POST',
        credentials: 'include',
        body: formData,
    });

    const data = await response.json();
    return data;
}
